import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { LoadingScreen } from './components/LoadingScreen';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ScrollToTop } from './components/ScrollToTop';
import { AuthProvider } from './contexts/AuthContext';

// Lazy load pages for code splitting
const LandingPage = lazy(() => import('./pages/LandingPage').then(m => ({ default: m.LandingPage })));
const TemplateSelection = lazy(() => import('./pages/TemplateSelection.tsx').then(m => ({ default: m.TemplateSelection })));
const About = lazy(() => import('./pages/About').then(m => ({ default: m.About })));
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));

// New Pages
const CategoryTemplates = lazy(() => import('./pages/CategoryTemplates').then(m => ({ default: m.CategoryTemplates })));
const CreateGift = lazy(() => import('./pages/CreateGift').then(m => ({ default: m.CreateGift })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const GiftViewer = lazy(() => import('./pages/GiftViewer').then(m => ({ default: m.GiftViewer })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const VideoTemplates = lazy(() => import('./pages/VideoTemplates.tsx').then(m => ({ default: m.VideoTemplates })));
const VideoGeneratorController = lazy(() => import('./pages/VideoGeneratorController.tsx').then(m => ({ default: m.VideoGeneratorController })));

function AppContent() {
  const location = useLocation();
  // No artificial loading delay needed as we use Suspense for lazy components
  const isGiftViewer = /^\/[^/]+\/[^/]+$/.test(location.pathname) && !location.pathname.startsWith('/templates/');

  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground neo-fade-in bg-[linear-gradient(to_right,#80808022_1px,transparent_1px),linear-gradient(to_bottom,#80808022_1px,transparent_1px)] bg-[size:24px_24px]">
      <ScrollToTop />

      {!isGiftViewer && <Header />}

      <main className="flex-grow flex flex-col items-center justify-center relative">
        <Suspense fallback={isGiftViewer ? null : <LoadingScreen />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/templates" element={<TemplateSelection />} />
            <Route path="/templates/:categoryId" element={<CategoryTemplates />} />
            <Route path="/create/:templateId" element={<CreateGift />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/about" element={<About />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/video-templates" element={<VideoTemplates />} />
            <Route path="/video-generator/:templateId" element={<VideoGeneratorController />} />
            <Route path="/:username/:giftName" element={<GiftViewer />} />
          </Routes>
        </Suspense>
      </main>

      {!isGiftViewer && <Footer />}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
